import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Info,
  ListChecks,
  RefreshCw,
  Search,
  TrendingUp,
  Users
} from 'lucide-react';
import { profitSharingAPI } from '../services/api';
import { formatMonthLabel, formatUnitCount, normalizeAutogeneratedDeviceName } from '../utils/i18nFormatters';
import { buildProfitSharingAccumulationView } from '../utils/profitSharingAccumulationView';

const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const pageSizeOptions = [5, 10, 20];

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatInteger = (value) => Math.round(toNumber(value)).toLocaleString('en-PH');
const formatRb = (value, unavailableLabel = '--') => (
  value === null || value === undefined
    ? unavailableLabel
    : `Rb ${formatInteger(toNumber(value) / 1000)}`
);
const unavailableCopy = () => 'Not currently calculable';

const formatTonsValue = (liters) => {
  const tons = toNumber(liters) / 1000;
  const rounded = Math.round(tons * 10) / 10;
  return Number.isInteger(rounded)
    ? rounded.toLocaleString('en-PH')
    : rounded.toLocaleString('en-PH', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-PH', {
    timeZone: 'Asia/Manila',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const ThresholdBar = ({ totalVolume, freeThreshold, t }) => {
  const volume = toNumber(totalVolume);
  const threshold = toNumber(freeThreshold) || 17010;
  const percent = Math.min(100, threshold > 0 ? (volume / threshold) * 100 : 0);

  return (
    <div className="inline-flex w-32 flex-col items-end gap-1">
      <div className="h-2 w-full overflow-hidden rounded-lg bg-[var(--admin-surface-soft)]">
        <div
          className={`h-full ${volume >= threshold ? 'bg-emerald-500' : 'bg-amber-400'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs font-bold text-[var(--admin-text-muted)]">
        {percent.toFixed(0)}% / {formatTonsValue(threshold)} {t('profitSharingFinance.units.tons')}
      </span>
    </div>
  );
};

function SummaryCard({ card }) {
  return (
    <article className="admin-muted-panel min-w-0 rounded-lg p-4">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="break-words text-sm font-black text-[var(--admin-text-primary)]">{card.title}</h4>
          <p className="mt-2 break-words text-xs leading-5 text-[var(--admin-text-muted)]">{card.hint}</p>
        </div>
        <span
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[var(--admin-border)] text-[var(--admin-text-muted)]"
          title={card.hint}
          aria-label={card.hint}
        >
          <Info size={14} />
        </span>
      </div>
      <div className="mt-4 break-words text-2xl font-black leading-tight text-[var(--admin-text-primary)]">
        {card.value}
      </div>
    </article>
  );
}

export default function ProfitSharingFinance() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [searchParams] = useSearchParams();
  const scope = searchParams.get('scope') === 'year' ? 'year' : 'month';
  const [year, setYear] = useState(Number(searchParams.get('year')) || now.getFullYear());
  const [month, setMonth] = useState(Number(searchParams.get('month')) || now.getMonth() + 1);
  const [dashboard, setDashboard] = useState(null);
  const [role, setRole] = useState(searchParams.get('account') === 'Headquarters' ? 'HQ' : 'HQ');
  const [roleSummary, setRoleSummary] = useState(null);
  const [financialDetails, setFinancialDetails] = useState(null);
  const [deviceFilters, setDeviceFilters] = useState({
    search: '',
    revenue: 'all',
    volume: 'all',
    threshold: 'all'
  });
  const [devicePage, setDevicePage] = useState(1);
  const [devicePageSize, setDevicePageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [moduleErrors, setModuleErrors] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    setModuleErrors([]);
    const requests = scope === 'year'
      ? [profitSharingAPI.getFinanceDashboard({ scope, year }), Promise.resolve({ data: { data: {} } }), Promise.resolve({ data: { data: {} } })]
      : [
        profitSharingAPI.getFinanceDashboard({ scope, year, month }),
        profitSharingAPI.getRoleSummary({ role, year, month }),
        profitSharingAPI.getFinancialDetails({ role, year, month })
      ];
    const labels = ['dashboard', 'roleSummary', 'financialDetails'];
    const results = await Promise.allSettled(requests);
    const setters = [setDashboard, setRoleSummary, setFinancialDetails];
    const failures = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        setters[index](result.value.data?.data || {});
        return;
      }
      setters[index](null);
      failures.push({
        module: labels[index],
        message: result.reason?.response?.data?.message
          || result.reason?.message
          || t('profitSharingFinance.errors.load')
      });
    });

    setModuleErrors(failures);
    if (results[0].status === 'rejected') {
      setError(failures[0]?.message || t('profitSharingFinance.errors.load'));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, year, month, role]);

  const roles = useMemo(() => [
    { key: 'HQ', label: t('profitSharingFinance.roles.HQ') },
    { key: 'Steward', label: t('profitSharingFinance.roles.Steward') },
    { key: 'RP', label: t('profitSharingFinance.roles.RP') },
    { key: 'PUSKUD', label: t('profitSharingFinance.roles.PUSKUD') }
  ], [t]);

  const roleRows = role === 'HQ'
    ? [
      ...(roleSummary?.stewards || []).map((item) => ({ ...item, roleType: 'Steward' })),
      ...(roleSummary?.rps || []).map((item) => ({ ...item, roleType: 'RP' })),
      ...(roleSummary?.puskuds || []).map((item) => ({ ...item, roleType: 'PUSKUD' }))
    ]
    : (roleSummary?.roles || []);

  const monthKey = scope === 'year' ? String(year) : `${year}-${String(month).padStart(2, '0')}`;
  const devices = useMemo(() => (
    Array.isArray(dashboard?.devices) ? dashboard.devices : []
  ), [dashboard]);

  const filteredDevices = useMemo(() => devices.filter((device) => {
    const keyword = deviceFilters.search.trim().toLowerCase();
    const searchable = [
      device.deviceId,
      device.unitId,
      device.deviceName,
      device.location
    ].filter(Boolean).join(' ').toLowerCase();
    const revenue = toNumber(device.totalRevenue);
    const volume = toNumber(device.totalVolume);
    const threshold = toNumber(device.freeThresholdVolume) || 17010;

    if (keyword && !searchable.includes(keyword)) return false;
    if (deviceFilters.revenue === 'withRevenue' && revenue <= 0) return false;
    if (deviceFilters.revenue === 'noRevenue' && revenue > 0) return false;
    if (deviceFilters.volume === 'withVolume' && volume <= 0) return false;
    if (deviceFilters.volume === 'noVolume' && volume > 0) return false;
    if (deviceFilters.threshold === 'reached' && volume < threshold) return false;
    if (deviceFilters.threshold === 'below' && volume >= threshold) return false;
    return true;
  }), [devices, deviceFilters]);

  const totalDevicePages = Math.max(1, Math.ceil(filteredDevices.length / devicePageSize));
  const safeDevicePage = Math.min(devicePage, totalDevicePages);
  const pagedDevices = filteredDevices.slice(
    (safeDevicePage - 1) * devicePageSize,
    safeDevicePage * devicePageSize
  );
  const deviceStart = filteredDevices.length === 0 ? 0 : (safeDevicePage - 1) * devicePageSize + 1;
  const deviceEnd = Math.min(safeDevicePage * devicePageSize, filteredDevices.length);

  useEffect(() => {
    if (devicePage > totalDevicePages) setDevicePage(totalDevicePages);
  }, [devicePage, totalDevicePages]);

  const updateDeviceFilter = (key, value) => {
    setDeviceFilters((prev) => ({ ...prev, [key]: value }));
    setDevicePage(1);
  };

  const updatePageSize = (value) => {
    setDevicePageSize(Number(value));
    setDevicePage(1);
  };

  const accumulationView = buildProfitSharingAccumulationView(dashboard || {});
  const notCalculable = unavailableCopy(i18n.language);
  const accumulationTitle = accumulationView.isYtd
    ? t('profitSharingFinance.summaryGroups.accumulation.ytdTitle')
    : t('profitSharingFinance.summaryGroups.accumulation.annualTitle', { year: accumulationView.year || year });
  const accumulationSubtitle = accumulationView.isYtd
    ? t('profitSharingFinance.summaryGroups.accumulation.ytdSubtitle', {
      startDate: accumulationView.period?.startDate || '-',
      endDate: accumulationView.period?.endDate || '-',
      timezone: accumulationView.period?.timezone || 'Asia/Manila',
      asOf: formatDateTime(accumulationView.period?.asOf, i18n.language)
    })
    : t('profitSharingFinance.summaryGroups.accumulation.annualSubtitle', {
      year: accumulationView.year || year,
      startDate: accumulationView.period?.startDate || '-',
      endDate: accumulationView.period?.endDate || '-'
    });

  const summaryGroups = [
    {
      key: 'accumulation',
      title: accumulationTitle,
      subtitle: accumulationSubtitle,
      reconciliation: accumulationView.reconciliation,
      cards: [
        { title: t('profitSharingFinance.summary.accumulationRevenue.title'), value: formatRb(accumulationView.totals?.totalRevenue, notCalculable), hint: t('profitSharingFinance.summary.accumulationRevenue.hint') },
        { title: t('profitSharingFinance.summary.accumulationSteward.title'), value: formatRb(accumulationView.totals?.stewardPayable, notCalculable), hint: t('profitSharingFinance.summary.accumulationSteward.hint') },
        { title: t('profitSharingFinance.summary.accumulationKud.title'), value: formatRb(accumulationView.totals?.kudPayable, notCalculable), hint: t('profitSharingFinance.summary.accumulationKud.hint') },
        { title: t('profitSharingFinance.summary.accumulationPuskud.title'), value: formatRb(accumulationView.totals?.puskudPayable, notCalculable), hint: t('profitSharingFinance.summary.accumulationPuskud.hint') }
      ]
    },
    {
      key: 'month',
      title: t('profitSharingFinance.summaryGroups.month.title', { month: monthKey }),
      subtitle: t('profitSharingFinance.summaryGroups.month.subtitle'),
      cards: [
        { title: t('profitSharingFinance.summary.monthRevenue.title', { month: monthKey }), value: formatRb(dashboard?.totalRevenue, notCalculable), hint: t('profitSharingFinance.summary.monthRevenue.hint') },
        { title: t('profitSharingFinance.summary.monthSteward.title'), value: formatRb(dashboard?.stewardPayable, notCalculable), hint: t('profitSharingFinance.summary.monthSteward.hint') },
        { title: t('profitSharingFinance.summary.monthKud.title'), value: formatRb(dashboard?.kudPayable, notCalculable), hint: t('profitSharingFinance.summary.monthKud.hint') },
        { title: t('profitSharingFinance.summary.monthPuskud.title'), value: formatRb(dashboard?.puskudPayable, notCalculable), hint: t('profitSharingFinance.summary.monthPuskud.hint') }
      ]
    }
  ];

  const ledgerRows = Array.isArray(financialDetails?.ledger) ? financialDetails.ledger : [];

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h2 className="flex min-w-0 items-center gap-3 break-words text-2xl font-black leading-tight text-[var(--admin-text-primary)] sm:text-3xl">
            <TrendingUp className="shrink-0 text-[var(--admin-accent)]" size={30} />
            {t('profitSharing.title')}
          </h2>
          <p className="mt-2 max-w-4xl break-words text-sm leading-6 text-[var(--admin-text-muted)]">{t('profitSharingFinance.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={year}
            onChange={(event) => {
              setYear(Number(event.target.value));
              setDevicePage(1);
            }}
            className="admin-input min-h-11 rounded-lg px-3 py-2 text-sm font-semibold outline-none"
          >
            {Array.from({ length: Math.max(1, now.getFullYear() - 2024) }, (_, index) => 2025 + index).map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          {scope === 'month' && (
            <select
              value={month}
              onChange={(event) => {
                setMonth(Number(event.target.value));
                setDevicePage(1);
              }}
              className="admin-input min-h-11 rounded-lg px-3 py-2 text-sm font-semibold outline-none"
            >
              {monthOptions.map((item) => <option key={item} value={item}>{formatMonthLabel(item, i18n.language)}</option>)}
            </select>
          )}
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] px-4 py-2 text-sm font-black text-[var(--admin-text-primary)] shadow-sm transition hover:border-[var(--admin-accent)]"
            title={t('common.refresh')}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
        </div>
      </header>

      {scope === 'year' && accumulationView.period && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm font-bold leading-6 text-sky-950" role="status">
          {t(accumulationView.isYtd ? 'profitSharingFinance.yearScopeNoteYtd' : 'profitSharingFinance.yearScopeNoteAnnual', { year })}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
          {error}
        </div>
      )}
      {moduleErrors.length > 0 && !error && (
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
          {notCalculable}: {moduleErrors.map((item) => item.module).join(', ')}
        </div>
      )}

      <section className="space-y-4" aria-label={t('profitSharingFinance.summarySection')}>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {summaryGroups.map((group) => (
            <article key={group.key} className="admin-card rounded-lg p-4 sm:p-5">
              <div className="mb-4 min-w-0">
                <h3 className="break-words text-base font-black text-[var(--admin-text-primary)]">{group.title}</h3>
                <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{group.subtitle}</p>
                {group.reconciliation && (
                  <p
                    data-testid="accumulation-reconciliation"
                    role="status"
                    className={`mt-3 inline-flex rounded-lg border px-3 py-1.5 text-xs font-black ${
                      !group.reconciliation.available
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : group.reconciliation.balanced
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                          : 'border-rose-200 bg-rose-50 text-rose-900'
                    }`}
                  >
                    {t(!group.reconciliation.available
                      ? 'profitSharingFinance.reconciliation.unavailable'
                      : group.reconciliation.balanced
                        ? 'profitSharingFinance.reconciliation.balanced'
                        : 'profitSharingFinance.reconciliation.mismatch')}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.cards.map((card) => (
                  <SummaryCard key={card.title} card={card} />
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="admin-card overflow-hidden rounded-lg" aria-label={t('profitSharingFinance.roleSummary.title')}>
        <div className="flex flex-col gap-4 border-b border-[var(--admin-border)] p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--admin-surface-soft)] text-[var(--admin-accent)]">
              <Users size={20} />
            </span>
            <div className="min-w-0">
              <h4 className="break-words text-base font-black text-[var(--admin-text-primary)]">{t('profitSharingFinance.roleSummary.title')}</h4>
              <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{t('profitSharingFinance.roleSummary.subtitle')}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {roles.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setRole(item.key)}
                className={`rounded-lg px-4 py-2 text-sm font-black transition-colors ${role === item.key ? 'bg-[var(--admin-accent)] text-white' : 'border border-[var(--admin-border)] bg-[var(--admin-surface)] text-[var(--admin-text-primary)] hover:border-[var(--admin-accent)]'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('common.loading')}</div>
          ) : roleRows.length === 0 ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.roleSummary.empty')}</div>
          ) : roleRows.map((item, index) => (
            <article key={`${item.roleType || role}-mobile-${item.userId || index}`} className="admin-muted-panel rounded-lg p-4">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  {role === 'HQ' && <p className="text-xs font-black text-[var(--admin-accent)]">{t(`profitSharingFinance.roles.${item.roleType}`, { defaultValue: item.roleType })}</p>}
                  <h5 className="mt-1 break-words text-sm font-black text-[var(--admin-text-primary)]">{item.userName || 'Unknown'}</h5>
                  <p className="mt-1 break-all font-mono text-xs text-[var(--admin-text-muted)]">{item.phoneNumber || '-'}</p>
                </div>
                <span className="shrink-0 rounded-lg bg-[var(--admin-surface)] px-2 py-1 text-xs font-black text-[var(--admin-text-muted)]">
                  {formatInteger(item.deviceCount || 0)}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-[var(--admin-text-muted)]">{t('profitSharingFinance.roleSummary.table.payable')}</span>
                  <span className="text-right font-black text-[var(--admin-text-primary)]">{formatRb(item.totalProfit)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[var(--admin-text-muted)]">{t('profitSharingFinance.roleSummary.table.revenue')}</span>
                  <span className="text-right font-black text-[var(--admin-text-primary)]">{formatRb(item.totalRevenue)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[var(--admin-text-muted)]">{t('profitSharingFinance.roleSummary.table.volume')}</span>
                  <span className="text-right font-black text-[var(--admin-text-primary)]">{formatTonsValue(item.totalVolume)} {t('profitSharingFinance.units.tons')}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[860px] text-left">
            <thead className="admin-muted-panel text-xs font-black text-[var(--admin-text-muted)]">
              <tr>
                {role === 'HQ' && <th className="p-4">{t('profitSharingFinance.roleSummary.table.role')}</th>}
                <th className="p-4">{t('profitSharingFinance.roleSummary.table.name')}</th>
                <th className="p-4">{t('profitSharingFinance.roleSummary.table.phone')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.roleSummary.table.devices')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.roleSummary.table.payable')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.roleSummary.table.revenue')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.roleSummary.table.volume')}</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr><td colSpan={role === 'HQ' ? 7 : 6} className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('common.loading')}</td></tr>
              ) : roleRows.length === 0 ? (
                <tr><td colSpan={role === 'HQ' ? 7 : 6} className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.roleSummary.empty')}</td></tr>
              ) : roleRows.map((item, index) => (
                <tr key={`${item.roleType || role}-${item.userId || index}`} className="border-t border-[var(--admin-border)] transition-colors hover:bg-[var(--admin-surface-soft)]">
                  {role === 'HQ' && <td className="p-4 font-bold text-[var(--admin-text-primary)]">{t(`profitSharingFinance.roles.${item.roleType}`, { defaultValue: item.roleType })}</td>}
                  <td className="p-4">
                    <div className="max-w-[240px] break-words font-black text-[var(--admin-text-primary)]">{item.userName || 'Unknown'}</div>
                  </td>
                  <td className="p-4 break-all font-mono text-xs text-[var(--admin-text-muted)]">{item.phoneNumber || '-'}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatInteger(item.deviceCount || 0)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(item.totalProfit)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(item.totalRevenue)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatTonsValue(item.totalVolume)} {t('profitSharingFinance.units.tons')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-card overflow-hidden rounded-lg" aria-label={t('profitSharingFinance.deviceMonthly.title')}>
        <div className="flex flex-col gap-2 border-b border-[var(--admin-border)] p-4 sm:p-5 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h4 className="break-words text-base font-black text-[var(--admin-text-primary)]">{t('profitSharingFinance.deviceMonthly.title')}</h4>
            <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{t('profitSharingFinance.deviceMonthly.subtitle')}</p>
          </div>
          <span className="text-sm font-bold text-[var(--admin-text-muted)]">{loading ? t('common.loading') : formatUnitCount(t, filteredDevices.length)}</span>
        </div>
        <div className="grid grid-cols-1 gap-3 border-b border-[var(--admin-border)] p-4 md:grid-cols-2 xl:grid-cols-[1fr_180px_180px_190px]">
          <label className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--admin-text-muted)]" />
            <input
              value={deviceFilters.search}
              onChange={(event) => updateDeviceFilter('search', event.target.value)}
              placeholder={t('profitSharingFinance.deviceMonthly.filters.searchPlaceholder')}
              className="admin-input w-full rounded-lg py-2 pl-9 pr-3 text-sm font-semibold outline-none"
            />
          </label>
          <select
            value={deviceFilters.revenue}
            onChange={(event) => updateDeviceFilter('revenue', event.target.value)}
            className="admin-input rounded-lg px-3 py-2 text-sm font-semibold outline-none"
          >
            <option value="all">{t('profitSharingFinance.deviceMonthly.filters.allRevenue')}</option>
            <option value="withRevenue">{t('profitSharingFinance.deviceMonthly.filters.withRevenue')}</option>
            <option value="noRevenue">{t('profitSharingFinance.deviceMonthly.filters.noRevenue')}</option>
          </select>
          <select
            value={deviceFilters.volume}
            onChange={(event) => updateDeviceFilter('volume', event.target.value)}
            className="admin-input rounded-lg px-3 py-2 text-sm font-semibold outline-none"
          >
            <option value="all">{t('profitSharingFinance.deviceMonthly.filters.allVolume')}</option>
            <option value="withVolume">{t('profitSharingFinance.deviceMonthly.filters.withVolume')}</option>
            <option value="noVolume">{t('profitSharingFinance.deviceMonthly.filters.noVolume')}</option>
          </select>
          <select
            value={deviceFilters.threshold}
            onChange={(event) => updateDeviceFilter('threshold', event.target.value)}
            className="admin-input rounded-lg px-3 py-2 text-sm font-semibold outline-none"
          >
            <option value="all">{t('profitSharingFinance.deviceMonthly.filters.allThreshold')}</option>
            <option value="reached">{t('profitSharingFinance.deviceMonthly.filters.reachedThreshold')}</option>
            <option value="below">{t('profitSharingFinance.deviceMonthly.filters.belowThreshold')}</option>
          </select>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('common.loading')}</div>
          ) : !devices.length ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.deviceMonthly.empty')}</div>
          ) : !filteredDevices.length ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.deviceMonthly.noMatch')}</div>
          ) : pagedDevices.map((device) => (
            <article key={`${device.unitId}-${device.deviceId}-mobile`} className="admin-muted-panel rounded-lg p-4">
              <h5 className="break-words text-sm font-black text-[var(--admin-text-primary)]">
                {normalizeAutogeneratedDeviceName(device.deviceName, device.deviceId || device.unitId || '-')}
              </h5>
              <p className="mt-1 break-all font-mono text-xs font-bold text-[var(--admin-accent)]">{device.deviceId || device.unitId}</p>
              <div className="mt-3 grid grid-cols-1 gap-2 text-sm">
                {[
                  ['revenue', formatRb(device.totalRevenue)],
                  ['volume', `${formatTonsValue(device.totalVolume)} ${t('profitSharingFinance.units.tons')}`],
                  ['steward', formatRb(device.stewardPayable)],
                  ['kud', formatRb(device.kudPayable)],
                  ['puskud', formatRb(device.puskudPayable)],
                  ['hq', formatRb(device.headquartersRevenue)]
                ].map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <span className="text-[var(--admin-text-muted)]">{t(`profitSharingFinance.deviceMonthly.table.${key}`)}</span>
                    <span className="text-right font-black text-[var(--admin-text-primary)]">{value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <ThresholdBar totalVolume={device.totalVolume} freeThreshold={device.freeThresholdVolume} t={t} />
              </div>
            </article>
          ))}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[1080px] text-left">
            <thead className="admin-muted-panel text-xs font-black text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('profitSharingFinance.deviceMonthly.table.device')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.revenue')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.volume')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.steward')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.kud')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.puskud')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.hq')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.deviceMonthly.table.threshold')}</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('common.loading')}</td></tr>
              ) : !devices.length ? (
                <tr><td colSpan="8" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.deviceMonthly.empty')}</td></tr>
              ) : !filteredDevices.length ? (
                <tr><td colSpan="8" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.deviceMonthly.noMatch')}</td></tr>
              ) : pagedDevices.map((device) => (
                <tr key={`${device.unitId}-${device.deviceId}`} className="border-t border-[var(--admin-border)] transition-colors hover:bg-[var(--admin-surface-soft)]">
                  <td className="p-4">
                    <div className="max-w-[280px] break-words font-black text-[var(--admin-text-primary)]">
                      {normalizeAutogeneratedDeviceName(device.deviceName, device.deviceId || device.unitId || '-')}
                    </div>
                    <div className="mt-1 break-all font-mono text-xs font-bold text-[var(--admin-accent)]">{device.deviceId || device.unitId}</div>
                  </td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(device.totalRevenue)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatTonsValue(device.totalVolume)} {t('profitSharingFinance.units.tons')}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(device.stewardPayable)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(device.kudPayable)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(device.puskudPayable)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(device.headquartersRevenue)}</td>
                  <td className="p-4 text-right"><ThresholdBar totalVolume={device.totalVolume} freeThreshold={device.freeThresholdVolume} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-[var(--admin-border)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-bold text-[var(--admin-text-muted)]">
            {t('profitSharingFinance.pagination.range', { start: deviceStart, end: deviceEnd, total: filteredDevices.length })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={devicePageSize}
              onChange={(event) => updatePageSize(event.target.value)}
              className="admin-input rounded-lg px-3 py-2 text-sm font-semibold outline-none"
              aria-label={t('profitSharingFinance.pagination.pageSize')}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{t('profitSharingFinance.pagination.perPage', { count: size })}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setDevicePage((prev) => Math.max(1, prev - 1))}
              disabled={safeDevicePage <= 1}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] text-[var(--admin-text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={t('profitSharingFinance.pagination.previous')}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="min-w-24 text-center text-sm font-black text-[var(--admin-text-primary)]">
              {t('profitSharingFinance.pagination.pageInfo', { page: safeDevicePage, totalPages: totalDevicePages })}
            </span>
            <button
              type="button"
              onClick={() => setDevicePage((prev) => Math.min(totalDevicePages, prev + 1))}
              disabled={safeDevicePage >= totalDevicePages}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface)] text-[var(--admin-text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={t('profitSharingFinance.pagination.next')}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <section className="admin-card overflow-hidden rounded-lg" aria-label={t('profitSharingFinance.detail.title')}>
        <div className="flex min-w-0 items-start gap-3 border-b border-[var(--admin-border)] p-4 sm:p-5">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--admin-surface-soft)] text-[var(--admin-accent)]">
            <ListChecks size={20} />
          </span>
          <div className="min-w-0">
            <h4 className="break-words text-base font-black text-[var(--admin-text-primary)]">{t('profitSharingFinance.detail.title')}</h4>
            <p className="mt-1 break-words text-sm leading-5 text-[var(--admin-text-muted)]">{t('profitSharingFinance.detail.subtitle', { role: roles.find((item) => item.key === role)?.label || role })}</p>
          </div>
        </div>
        <div className="space-y-3 p-4 md:hidden">
          {loading ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('common.loading')}</div>
          ) : ledgerRows.length === 0 ? (
            <div className="admin-muted-panel rounded-lg p-6 text-center text-sm font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.detail.empty')}</div>
          ) : ledgerRows.slice(0, 10).map((entry, index) => (
            <article key={`${entry.id || index}-mobile`} className="admin-muted-panel rounded-lg p-4">
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-black text-[var(--admin-accent)]">{t(`profitSharingFinance.roles.${entry.accountType}`, { defaultValue: entry.accountType || '-' })}</p>
                  <h5 className="mt-1 break-all font-mono text-xs font-bold text-[var(--admin-text-primary)]">{entry.deviceId || entry.unitId || '-'}</h5>
                </div>
                <span className="shrink-0 text-right text-sm font-black text-[var(--admin-text-primary)]">{formatRb(entry.amount)}</span>
              </div>
              <p className="mt-3 break-words text-sm leading-5 text-[var(--admin-text-primary)]">{entry.description || '-'}</p>
              <div className="mt-3 flex justify-between gap-3 text-xs text-[var(--admin-text-muted)]">
                <span>{formatDateTime(entry.createdAt, i18n.language)}</span>
                <span>{formatTonsValue(entry.volume)} {t('profitSharingFinance.units.tons')} / {entry.status || '-'}</span>
              </div>
            </article>
          ))}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[900px] text-left">
            <thead className="admin-muted-panel text-xs font-black text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('profitSharingFinance.detail.table.time')}</th>
                <th className="p-4">{t('profitSharingFinance.detail.table.role')}</th>
                <th className="p-4">{t('profitSharingFinance.detail.table.device')}</th>
                <th className="p-4">{t('profitSharingFinance.detail.table.description')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.detail.table.amount')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.detail.table.volume')}</th>
                <th className="p-4 text-right">{t('profitSharingFinance.detail.table.status')}</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('common.loading')}</td></tr>
              ) : ledgerRows.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center font-bold text-[var(--admin-text-muted)]">{t('profitSharingFinance.detail.empty')}</td></tr>
              ) : ledgerRows.slice(0, 10).map((entry, index) => (
                <tr key={entry.id || index} className="border-t border-[var(--admin-border)] transition-colors hover:bg-[var(--admin-surface-soft)]">
                  <td className="p-4 text-[var(--admin-text-muted)]">{formatDateTime(entry.createdAt, i18n.language)}</td>
                  <td className="p-4 font-black text-[var(--admin-text-primary)]">{t(`profitSharingFinance.roles.${entry.accountType}`, { defaultValue: entry.accountType || '-' })}</td>
                  <td className="p-4 break-all font-mono text-xs text-[var(--admin-accent)]">{entry.deviceId || entry.unitId || '-'}</td>
                  <td className="p-4">
                    <div className="max-w-[280px] break-words text-[var(--admin-text-primary)]">{entry.description || '-'}</div>
                  </td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatRb(entry.amount)}</td>
                  <td className="p-4 text-right font-black text-[var(--admin-text-primary)]">{formatTonsValue(entry.volume)} {t('profitSharingFinance.units.tons')}</td>
                  <td className="p-4 text-right font-bold text-[var(--admin-text-muted)]">{entry.status || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
